// Connectors (design §7): where completed documents go. Add one, fill in its settings, connect it (Salesforce /
// HubSpot sign-in, Microsoft admin consent, Infor .ionapi file, or nothing for webhooks and S3), test, turn on/off.
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronRight, FileUp, Link2, Plug, PlugZap, Plus, RotateCcw, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { CONNECTOR_INFO, fullDate } from "../lib/format";
import type { AdminOverview, Connector, ConnectorType } from "../lib/types";
import { Card, Chip, inputCls } from "../components/bits";
import { Button, ConfirmDialog, cx, Empty, Modal, toast } from "../components/ui";
import { ConnectorDot } from "../components/Connectors";
import { CopyLine } from "./CompanySso";

type Settings = Record<string, any>;
interface FieldDef {
  key: string;
  label: string;
  hint?: string;
  placeholder?: string;
  kind?: "text" | "select" | "checkbox" | "textarea" | "events";
  options?: [string, string][];
}

const EVENTS: [string, string][] = [
  ["envelope.completed", "Completed (everyone signed)"],
  ["envelope.declined", "Declined"],
  ["envelope.voided", "Voided"],
  ["envelope.expired", "Expired"],
];

const FIELDS: Record<ConnectorType, FieldDef[]> = {
  webhook: [
    { key: "url", label: "Endpoint URL", placeholder: "https://example.com/esignit-webhook" },
    { key: "events", label: "Send these events", kind: "events" },
    { key: "includePdf", label: "Include the signed PDF itself (up to 5 MB) as well as a download link", kind: "checkbox" },
  ],
  salesforce: [
    { key: "environment", label: "Org", kind: "select", options: [["production", "Production / Developer Edition"], ["sandbox", "Sandbox"]] },
    { key: "object", label: "Object", placeholder: "Opportunity", hint: "API name of the object senders link to: Opportunity, Account, Contract, or a custom object like Agreement__c." },
    { key: "field", label: "Field to set when signed (optional)", placeholder: "Signature_Completed__c" },
    { key: "fieldValue", label: "Value", placeholder: "Yes" },
    { key: "chatter", label: "Post to the record's Chatter feed", kind: "checkbox" },
  ],
  hubspot: [
    { key: "property", label: "Deal property to set when signed (optional)", placeholder: "signature_completed" },
    { key: "propertyValue", label: "Value", placeholder: "true" },
  ],
  dynamics: [
    { key: "environmentUrl", label: "Environment URL", placeholder: "https://yourorg.crm.dynamics.com" },
    { key: "table", label: "Table (logical name)", placeholder: "opportunity" },
    { key: "entitySet", label: "Entity set name", placeholder: "opportunities" },
    { key: "nameColumn", label: "Name column", placeholder: "name" },
    { key: "idColumn", label: "Id column", placeholder: "opportunityid" },
    { key: "column", label: "Column to set when signed (optional)", placeholder: "new_signaturecompleted" },
    { key: "columnValue", label: "Value", placeholder: "true" },
  ],
  sharepoint: [
    { key: "siteUrl", label: "Site URL", placeholder: "https://contoso.sharepoint.com/sites/Contracts" },
    { key: "library", label: "Document library", placeholder: "Documents" },
    { key: "folder", label: "Folder", placeholder: "eSignIt/{year}", hint: "Placeholders: {year}, {month}, {title}, {sender}, {record}." },
  ],
  s3: [
    { key: "roleArn", label: "Role ARN in your account", placeholder: "arn:aws:iam::123456789012:role/eSignItDelivery" },
    { key: "bucket", label: "Bucket", placeholder: "acme-signed-contracts" },
    { key: "region", label: "Region", placeholder: "us-east-1" },
    { key: "prefix", label: "Prefix", placeholder: "esignit/{year}", hint: "Files go to {prefix}/{envelope id}/. Placeholders: {year}, {month}." },
  ],
  infor: [
    { key: "entityName", label: "IDM document type", placeholder: "MDS_GenericDocument" },
    { key: "recordAttribute", label: "IDM attribute for the record reference (optional)", placeholder: "ContractId" },
    { key: "acl", label: "IDM access control list", placeholder: "Public" },
    { key: "updateMethod", label: "Landmark update method", kind: "select", options: [["POST", "POST"], ["PUT", "PUT"], ["PATCH", "PATCH"]] },
    { key: "updatePath", label: "Landmark update path (optional)", placeholder: "/FSM/fsm/soap/classes/Contract/actions/Update?Contract={recordId}", hint: "Under your tenant's ION API URL. {recordId} is the reference the sender typed." },
    { key: "updateBody", label: "Update body (JSON)", kind: "textarea", placeholder: '{"SignatureEnvelope": "{envelopeId}"}', hint: "Placeholders: {recordId}, {envelopeId}, {completedAt}, {title}." },
  ],
};

const DEFAULTS: Partial<Record<ConnectorType, Settings>> = {
  webhook: { events: ["envelope.completed"] },
  salesforce: { environment: "production", object: "Opportunity", chatter: true },
  dynamics: { table: "opportunity" },
  sharepoint: { library: "Documents", folder: "eSignIt/{year}" },
  s3: { region: "us-east-1", prefix: "esignit/{year}" },
  infor: { entityName: "MDS_GenericDocument", acl: "Public", updateMethod: "POST" },
};

/** Clean up before sending: empty strings dropped, Dynamics values typed. */
function outgoing(type: ConnectorType, s: Settings): Settings {
  const out: Settings = {};
  for (const [k, v] of Object.entries(s)) if (v !== "" && v !== undefined) out[k] = v;
  if (type === "dynamics" && typeof out.columnValue === "string" && /^(true|false)$/i.test(out.columnValue)) out.columnValue = out.columnValue.toLowerCase() === "true";
  return out;
}

function SettingsForm({ type, value, onChange }: { type: ConnectorType; value: Settings; onChange: (v: Settings) => void }) {
  return (
    <div className="space-y-3">
      {FIELDS[type].map((f) => (
        <div key={f.key} className="text-sm">
          {f.kind === "checkbox" ? (
            <label className="flex items-start gap-2">
              <input type="checkbox" className="mt-1" checked={Boolean(value[f.key])} onChange={(e) => onChange({ ...value, [f.key]: e.target.checked })} />
              <span>{f.label}</span>
            </label>
          ) : f.kind === "events" ? (
            <fieldset>
              <legend className="font-medium text-ink-2 mb-1">{f.label}</legend>
              <div className="grid sm:grid-cols-2 gap-1">
                {EVENTS.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={(value.events ?? []).includes(k)}
                      onChange={(e) => onChange({ ...value, events: e.target.checked ? [...(value.events ?? []), k] : (value.events ?? []).filter((x: string) => x !== k) })}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </fieldset>
          ) : (
            <label className="block">
              <span className="block font-medium text-ink-2 mb-1">{f.label}</span>
              {f.kind === "select" ? (
                <select value={value[f.key] ?? f.options![0][0]} onChange={(e) => onChange({ ...value, [f.key]: e.target.value })} className={inputCls}>
                  {f.options!.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              ) : f.kind === "textarea" ? (
                <textarea value={value[f.key] ?? ""} placeholder={f.placeholder} onChange={(e) => onChange({ ...value, [f.key]: e.target.value })} rows={3} className={cx(inputCls, "h-auto py-2 font-mono text-xs")} />
              ) : (
                <input value={value[f.key] === undefined ? "" : String(value[f.key])} placeholder={f.placeholder} onChange={(e) => onChange({ ...value, [f.key]: e.target.value })} className={inputCls} />
              )}
              {f.hint && <span className="block text-xs text-muted mt-1">{f.hint}</span>}
            </label>
          )}
        </div>
      ))}
    </div>
  );
}

/** What the admin must set up on the other side, per type. */
function SetupNotes({ c }: { c: Connector }) {
  const sendWith = `${location.origin}/new?connector=${c.id}&record=`;
  switch (c.type) {
    case "webhook":
      return (
        <div className="space-y-2">
          {c.reveal?.signingSecret && <CopyLine label="Signing secret (check the x-esignit-signature header with it)" value={c.reveal.signingSecret} />}
          <p className="text-xs text-muted">Each POST has x-esignit-signature: t=…,v1=HMAC-SHA256(secret, "t.body"). Answer 2xx within 10 seconds; anything else is retried for about an hour (4xx is not retried). Use x-esignit-delivery to ignore duplicates.</p>
        </div>
      );
    case "s3":
      return (
        <div className="space-y-2">
          {c.reveal?.trustRole && <CopyLine label="Trust this eSignIt role" value={c.reveal.trustRole} />}
          {c.reveal?.externalId && <CopyLine label="External ID (required in the trust policy)" value={c.reveal.externalId} />}
          <p className="text-xs text-muted">Create a role in your AWS account whose trust policy allows sts:AssumeRole for the role above with the condition sts:ExternalId = the External ID, and whose permissions allow s3:PutObject on your bucket and prefix.</p>
        </div>
      );
    case "salesforce":
      return (
        <div className="space-y-2">
          <CopyLine label='"Send with eSignIt" button URL (Opportunity detail page button)' value={`${sendWith}{!Opportunity.Id}`} />
          <p className="text-xs text-muted">Connect signs in to your org through eSignIt's app (OAuth). Use an integration user that can read the object, create Files and update the field.</p>
        </div>
      );
    case "hubspot":
      return <p className="text-xs text-muted">Connect installs eSignIt's HubSpot app in your portal (deals and files scopes).</p>;
    case "dynamics":
      return <p className="text-xs text-muted">Connect asks a Microsoft admin to consent for your tenant. Then, in the Power Platform admin center, add eSignIt as an application user in this environment with a security role that can read the table and create notes.</p>;
    case "sharepoint":
      return <p className="text-xs text-muted">Connect asks a Microsoft admin to consent (Sites.Selected). Then grant eSignIt write access to this one site (Graph: POST /sites/{"{site-id}"}/permissions with role "write" for eSignIt's app).</p>;
    case "infor":
      return <p className="text-xs text-muted">In ION API, create a Backend Service authorized app for eSignIt, add a service account, download its .ionapi file and upload it here. The account needs IDM create rights (and Landmark update rights if you set an update).</p>;
  }
}

function ConnectorRow({ c, open, onToggle, reload }: { c: Connector; open: boolean; onToggle: () => void; reload: () => Promise<void> }) {
  const [settings, setSettings] = useState<Settings>(c.settings);
  const [name, setName] = useState(c.name);
  const [busy, setBusy] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);
  const [detail, setDetail] = useState<Connector>(c);
  useEffect(() => {
    if (open) api<Connector>(`/admin/connectors/${c.id}`).then(setDetail, () => undefined);
  }, [open, c.id, c.updatedAt]);
  const run = async (key: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(key);
    try {
      const r: any = await fn();
      if (ok) toast(ok);
      if (r && typeof r === "object" && "ok" in r) toast(r.message, r.ok ? "ok" : "error");
      await reload();
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(null);
    }
  };
  const tone = c.status === "connected" ? "good" : c.status === "error" ? "bad" : "warn";
  return (
    <div className="border border-line rounded-xl bg-surface">
      <button className="w-full flex items-center gap-3 px-4 py-3 text-left" onClick={onToggle} aria-expanded={open}>
        {open ? <ChevronDown className="size-4 text-muted" /> : <ChevronRight className="size-4 text-muted" />}
        <ConnectorDot type={c.type} />
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">{c.name}</div>
          <div className="text-xs text-muted truncate">
            {c.label}
            {c.account ? ` · ${c.account}` : ""}
          </div>
        </div>
        {!c.enabled && <Chip>Off</Chip>}
        <Chip tone={tone}>{c.status === "connected" ? "Connected" : c.status === "error" ? "Error" : "Needs setup"}</Chip>
      </button>
      {open && (
        <div className="border-t border-line px-4 py-4 grid lg:grid-cols-2 gap-5">
          <div className="space-y-3">
            <label className="block text-sm">
              <span className="block font-medium text-ink-2 mb-1">Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
            </label>
            <SettingsForm type={c.type} value={settings} onChange={setSettings} />
            <div className="flex gap-2 flex-wrap">
              <Button variant="primary" busy={busy === "save"} onClick={() => run("save", () => api(`/admin/connectors/${c.id}`, { body: { name, settings: outgoing(c.type, settings) } }), "Saved")}>
                Save
              </Button>
              <Button busy={busy === "enable"} onClick={() => run("enable", () => api(`/admin/connectors/${c.id}`, { body: { enabled: !c.enabled } }), c.enabled ? "Turned off" : "Turned on")}>
                {c.enabled ? "Turn off" : "Turn on"}
              </Button>
              <Button variant="ghost" className="text-bad" onClick={() => setRemoving(true)}>
                <Trash2 className="size-4" /> Remove
              </Button>
            </div>
          </div>
          <div className="space-y-4">
            {c.lastError && <p className="text-sm text-bad break-words">Last error: {c.lastError}</p>}
            <div className="flex gap-2 flex-wrap">
              {c.oauth && (
                <Button
                  variant={c.status === "connected" ? "secondary" : "primary"}
                  busy={busy === "oauth"}
                  onClick={() =>
                    run("oauth", async () => {
                      await api(`/admin/connectors/${c.id}`, { body: { settings: outgoing(c.type, settings) } });
                      const { url } = await api<{ url: string }>(`/admin/connectors/${c.id}/oauth`, { body: {} });
                      location.assign(url);
                    })
                  }
                >
                  <PlugZap className="size-4" /> {c.status === "connected" ? "Reconnect" : c.oauth === "admin-consent" ? "Grant Microsoft consent" : `Connect ${c.label}`}
                </Button>
              )}
              {c.type === "infor" && (
                <label className="inline-flex items-center gap-1.5 rounded-lg border border-line px-3 h-9 text-sm font-medium cursor-pointer hover:bg-surface-2">
                  <FileUp className="size-4" /> {c.secretsSet ? "Replace .ionapi file" : "Upload .ionapi file"}
                  <input
                    type="file"
                    accept=".ionapi,application/json"
                    hidden
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (f) await run("ionapi", async () => api(`/admin/connectors/${c.id}`, { body: { secrets: { ionapi: await f.text() } } }), "Credentials saved");
                    }}
                  />
                </label>
              )}
              <Button busy={busy === "test"} onClick={() => run("test", () => api(`/admin/connectors/${c.id}/test`, { body: {} }))} disabled={c.status === "setup" && c.type !== "webhook" && c.type !== "s3"}>
                Test connection
              </Button>
              {c.type === "webhook" && (
                <Button busy={busy === "rotate"} onClick={() => run("rotate", () => api(`/admin/connectors/${c.id}`, { body: { rotateSecret: true } }), "New signing secret made")}>
                  <RotateCcw className="size-4" /> New secret
                </Button>
              )}
            </div>
            <SetupNotes c={detail} />
            <p className="text-xs text-muted">
              Added {fullDate(c.createdAt)}. {CONNECTOR_INFO[c.type]?.blurb}
            </p>
          </div>
        </div>
      )}
      {removing && (
        <ConfirmDialog
          title={`Remove ${c.name}?`}
          confirm="Remove"
          danger
          onClose={() => setRemoving(false)}
          onConfirm={async () => {
            await api(`/admin/connectors/${c.id}`, { method: "DELETE" });
            toast("Removed");
            await reload();
          }}
        >
          <p>Envelopes already sent there stay there. Envelopes linked to its records won't be delivered.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

export default function CompanyConnectors({ o, reload }: { o: AdminOverview; reload: () => Promise<void> }) {
  const [params] = useSearchParams();
  const [open, setOpen] = useState<string | null>(params.get("open"));
  const [adding, setAdding] = useState<ConnectorType | null>(null);
  const [settings, setSettings] = useState<Settings>({});
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const blocked = o.company.status !== "active" ? "You can add connectors once eSignIt approves your company." : null;
  const types = useMemo(() => o.connectorTypes, [o]);
  const add = async () => {
    if (!adding) return;
    setBusy(true);
    try {
      const c = await api<Connector>("/admin/connectors", { body: { type: adding, name: name || undefined, settings: outgoing(adding, settings) } });
      toast(`${c.name} added`);
      setAdding(null);
      await reload();
      setOpen(c.id);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-5">
      {blocked && <p className="text-sm text-muted">{blocked}</p>}
      {o.connectors.length === 0 ? (
        <Empty icon={<Plug className="size-10" />} title="No connectors yet">
          Send completed documents to your CRM, SharePoint, S3 or any system with a webhook.
        </Empty>
      ) : (
        <div className="space-y-2">
          {o.connectors.map((c) => (
            <ConnectorRow key={c.id + c.updatedAt} c={c} open={open === c.id} onToggle={() => setOpen(open === c.id ? null : c.id)} reload={reload} />
          ))}
        </div>
      )}
      <Card title="Add a connector">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {types.map((t) => {
            const disabled = Boolean(blocked) || !t.allowed || !t.available;
            return (
              <button
                key={t.type}
                disabled={disabled}
                onClick={() => (setAdding(t.type), setSettings(DEFAULTS[t.type] ?? {}), setName(""))}
                className="text-left rounded-xl border border-line p-3 hover:bg-surface-2 disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <div className="flex items-center gap-2 font-medium">
                  <ConnectorDot type={t.type} /> {t.label}
                  <Plus className="size-4 ml-auto text-muted" />
                </div>
                <p className="text-xs text-muted mt-1">{CONNECTOR_INFO[t.type]?.blurb}</p>
                {!t.allowed && <p className="text-xs text-warn mt-1">Team plan and up</p>}
                {t.allowed && !t.available && <p className="text-xs text-warn mt-1">Coming soon on this site</p>}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted mt-3 flex items-center gap-1.5">
          <Link2 className="size-3.5" /> Record connectors (Salesforce, HubSpot, Dynamics, Infor) act on envelopes linked to one of their records. SharePoint, S3 and webhooks act on every envelope.
        </p>
      </Card>
      {adding && (
        <Modal
          wide
          title={`Add ${types.find((t) => t.type === adding)?.label}`}
          onClose={() => setAdding(null)}
          footer={
            <>
              <Button onClick={() => setAdding(null)}>Cancel</Button>
              <Button variant="primary" busy={busy} onClick={add}>
                Add
              </Button>
            </>
          }
        >
          <div className="space-y-3 pb-2">
            <label className="block text-sm">
              <span className="block font-medium text-ink-2 mb-1">Name (optional)</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder={types.find((t) => t.type === adding)?.label} className={inputCls} />
            </label>
            <SettingsForm type={adding} value={settings} onChange={setSettings} />
          </div>
        </Modal>
      )}
    </div>
  );
}
